import { MicData, AzureSttResponse } from "@joyz/app-core/models/microphone";
import {
  ConversationProblemsState,
  ConversationProblemsResponseTime,
  PayloadExerciseConversationAnswer,
  PayloadExerciseInfo
} from "@tt-app/models/exercise";

/**
 * Utility to build User response time: the time between the end of bot speech
 * and the time of the first word from the user
 *
 * @param param0
 * @param micData
 */
export const buildRespTime = (
  { botEndTime, userStartTime }: ConversationProblemsResponseTime,
  micData: MicData[]
): number => {
  const respTimeFromBotToUser = botEndTime && userStartTime ? userStartTime - botEndTime : -1;

  const respTimeSpeechOffset =
    micData
      .filter(d => !d.deleted && d.recognition.value)
      .map(d => d.recognition.raw)
      // https://stackoverflow.com/a/54696488/4906586
      .filter((stt): stt is AzureSttResponse => {
        return (stt as AzureSttResponse).MaskedLexical !== undefined;
      })
      .map(d => d.IntermediateResults[0].Offset)
      // Strange unit of 100-nanoseconds
      .map(offset => offset / 10000)
      .find(() => true) || -1;

  const respTime =
    respTimeFromBotToUser + respTimeSpeechOffset > 0
      ? Math.round(respTimeFromBotToUser + respTimeSpeechOffset)
      : -1;

  return respTime;
};

/**
 * Utility to build the payload for submitting the chat problem user answer to the API
 *
 * @param chatProblemsState
 * @param authInfo
 * @param logId
 * @param respTime
 * @param micData
 */
export const buildChatAnswerPayload = (
  chatProblemsState: ConversationProblemsState,
  authInfo: PayloadExerciseInfo,
  logId: number,
  respTime: number,
  micData: MicData[]
): PayloadExerciseConversationAnswer => {
  // Check: Problem UUID
  const problemUuid = chatProblemsState.current ? chatProblemsState.current.problemUuid : "";
  if (!problemUuid) {
    throw new Error(`Current problem UUID is empty in state ${JSON.stringify(chatProblemsState)}`);
  }

  // Check: user response time
  if (respTime <= 0) {
    // TODO: problem here
  }

  // extract valid data only
  const validData = micData.filter(d => !d.deleted && d.recognition.value);

  return {
    ...authInfo,
    logId,
    problemUuid: problemUuid,
    repeated: chatProblemsState.retryCount,
    answer: validData.map(d => d.recognition.value).join(" "),
    displayAnswer: validData.map(d => d.recognition.display).join(" "),
    responseTime: respTime,
    rawInputs: validData.map(d => {
      const azureSttResp: AzureSttResponse | undefined =
        (d.recognition.raw as AzureSttResponse).MaskedLexical !== undefined
          ? (d.recognition.raw as AzureSttResponse)
          : undefined;

      const pronScore = azureSttResp
        ? azureSttResp.IntermediateResults.reduce((acc, curr) => acc + curr.Confidence, 0) /
          azureSttResp.IntermediateResults.length
        : -1;
      const seconds = azureSttResp
        ? azureSttResp.IntermediateResults.reduce((acc, curr) => acc + curr.Duration, 0) / 10000000
        : -1;

      return {
        // TODO: remove temporary code
        text: azureSttResp ? azureSttResp.MaskedLexical : "",
        // END OF TEMPORARY CODE
        displayText: azureSttResp ? azureSttResp.DisplayText : "",
        maskedDisplayText: azureSttResp ? azureSttResp.MaskedDisplayText : "",
        lexical: azureSttResp ? azureSttResp.Lexical : "",
        maskedLexical: azureSttResp ? azureSttResp.MaskedLexical : "",
        pronScore,
        utteranceUrl: d.recording.remoteUrl || "",
        seconds
      };
    })
  };
};
